import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';
import { TextField, Popover } from '@mui/material';
import { ModernCard, ModernCheckbox, ModernButton } from './ModernComponents';
import MapParamsSimulation from '../Result/Map/MapParamsSimulation';
import './styles/step_parcel_constraint.style.scss';
import FilterPanel from 'utils/MaterialUi/FilterPanel';
import MultiSelectComponent from '../Component/MultiSelectComponent';
import i18next from 'i18next';

const getConstraintTypes = () => [
  {
    value: 'obligation',
    label: i18next.t('simulation.step.parcelConstraints.constraintTypes.obligation'),
  },
  {
    value: 'interdiction',
    label: i18next.t('simulation.step.parcelConstraints.constraintTypes.interdiction'),
  },
  {
    value: 'division',
    label: i18next.t('simulation.step.parcelConstraints.constraintTypes.division'),
  },
  {
    value: 'permission',
    label: i18next.t('simulation.step.parcelConstraints.constraintTypes.permission'),
  },
  { value: 'conversion', label: i18next.t('simulation.common.transitionBio') },
];

export const getConstraintTypeLabel = (type) =>
  getConstraintTypes().find((ct) => ct.value === type)?.label || type;

const getCultureTypes = () => [
  { value: 'main', label: i18next.t('simulation.step.parcelConstraints.cultureTypes.main') },
  { value: 'intermediate', label: i18next.t('common.interculture') },
];

const FILTER_FIELDS = ['name', 'constraint', 'years', 'parcels', 'cultures'];

const typeClassMap = {
  obligation: 'constraintCardTypeObligation',
  interdiction: 'constraintCardTypeInterdiction',
  division: 'constraintCardTypeDivision',
  permission: 'constraintCardTypePermission',
  conversion: 'constraintCardTypeConversionBio',
};

const ConstraintCard = ({
  constraint,
  exploitationSelected,
  selectedCultures,
  years,
  editConstraint,
  deleteConstraint,
  t,
}) => (
  <div className="constraintCard">
    <div className="constraintCardHeader">
      <div className="constraintCardHeaderLeft">
        <span className={`constraintCardType ${typeClassMap[constraint.constraint] || ''}`}>
          {getConstraintTypeLabel(constraint.constraint)}
        </span>
        {constraint.name && <span className="constraintCardName">{constraint.name}</span>}
      </div>
      <div className="constraintCardActions">
        <button
          className="constraintCardActionBtn"
          onClick={() => editConstraint?.(constraint)}
          title={t('common.edit')}
        >
          <EditIcon sx={{ fontSize: 16 }} />
        </button>
        <button
          className="constraintCardActionBtn constraintCardActionBtnDelete"
          onClick={() => deleteConstraint(constraint.id)}
          title={t('common.delete')}
        >
          <DeleteIcon sx={{ fontSize: 16 }} />
        </button>
      </div>
    </div>
    <div className="constraintCardBody">
      <div className="constraintCardSection">
        <strong>{t('common.parcels')}:</strong>
        <span>
          {constraint?.parcels
            .map((id) => exploitationSelected?.parcels?.find((p) => p.id === id))
            .filter((p) => p && !p.parent)
            .map((p) => p.name)
            .join(', ')}
        </span>
      </div>
      {constraint.constraint !== 'conversion' && (
        <div className="constraintCardSection">
          <strong>{t('common.cultures')}:</strong>
          <span>
            {constraint?.cultures
              ?.map((id) => selectedCultures.find((c) => c.id === id)?.name)
              .filter(Boolean)
              .join(', ')}
          </span>
        </div>
      )}
      <div className="constraintCardSection">
        <strong>{t('common.campaigns')}:</strong>
        <span>
          {constraint?.years
            .map((id) => years.find((c) => c.id === id)?.label)
            .filter(Boolean)
            .join(', ')}
        </span>
      </div>
    </div>
  </div>
);

const StepParcelConstraint = ({
  mapCoordinates,
  parcelConstraints,
  showConstraintModal,
  currentConstraint,
  selectedParcels,
  exploitationSelected,
  toggleModal,
  deleteConstraint,
  updateConstraintField,
  toggleParcelConstraintField,
  saveConstraint,
  editConstraint,
  cultures,
  selectedCultureIds,
  forecastCampaign,
  yearsToSimulate,
}) => {
  const { t } = useTranslation();
  const [filterOpen, setFilterOpen] = useState(null);
  const [filters, setFilters] = useState({
    name: '',
    years: [],
    parcels: [],
    constraint: [],
    cultures: [],
  });
  const [activeFiltersList, setActiveFiltersList] = useState([]);
  const [hoveredParcels, setHoveredParcels] = useState(null);

  const years = useMemo(() => {
    if (!forecastCampaign?.startYear || !yearsToSimulate) return [];
    return Array.from({ length: yearsToSimulate }, (_, i) => {
      const startYear = forecastCampaign.startYear + i;
      const endYear = startYear + 1;
      return { id: String(endYear), label: `${startYear}-${endYear}`, startYear, endYear };
    });
  }, [forecastCampaign?.startYear, yearsToSimulate]);

  const selectedCultures = useMemo(() => {
    if (!cultures || !selectedCultureIds) return [];
    return cultures.filter((culture) => selectedCultureIds.includes(culture.id));
  }, [cultures, selectedCultureIds]);

  // To check if we display the conversion constraint
  const hasConventionalParcel = useMemo(
    () =>
      exploitationSelected?.parcels?.some(
        (parcel) => selectedParcels.includes(parcel.id) && parcel.organicState === 'conventional',
      ) ?? false,
    [exploitationSelected?.parcels, selectedParcels],
  );

  const availableConstraintTypes = useMemo(
    () =>
      getConstraintTypes().filter(
        (constraintType) => constraintType.value !== 'conversion' || hasConventionalParcel,
      ),
    [hasConventionalParcel],
  );

  const conversionBioYear = useMemo(() => {
    if (!years || years.length < 3) return null;
    return years[2];
  }, [years]);

  const availableParcelsWithChildren = useMemo(() => {
    if (!exploitationSelected?.parcels || !selectedParcels) return [];
    const filtered = exploitationSelected.parcels.filter(
      (p) => selectedParcels.includes(p.id) || selectedParcels.includes(p.parent),
    );
    if (currentConstraint?.constraint !== 'conversion') return filtered;
    return filtered.filter((p) => {
      const parent = p.parent ? filtered.find((parent) => parent.id === p.parent) : p;
      return parent?.organicState === 'conventional';
    });
  }, [exploitationSelected?.parcels, selectedParcels, currentConstraint?.constraint]);

  const availableParcels = useMemo(() => {
    return availableParcelsWithChildren.filter((p) => !p.parent);
  }, [availableParcelsWithChildren]);

  const selectedParcelsSurface = useMemo(() => {
    if (!exploitationSelected?.parcels || !currentConstraint?.parcels?.length) return 0;
    return exploitationSelected.parcels
      .filter((p) => currentConstraint.parcels.includes(p.id))
      .reduce((acc, p) => acc + (p.surface || 0), 0)
      .toFixed(2);
  }, [exploitationSelected?.parcels, currentConstraint?.parcels]);

  const filteredCultures = useMemo(() => {
    if (currentConstraint?.constraint === 'permission') {
      return selectedCultures.filter((c) => c.cultureType === 'intermediate');
    }
    if (currentConstraint?.constraint === 'obligation') {
      return selectedCultures.filter((c) => c.cultureType === currentConstraint?.cultureType);
    }
    return selectedCultures.filter((c) => c.cultureType === 'main');
  }, [selectedCultures, currentConstraint?.constraint, currentConstraint?.cultureType]);

  const cultureLabel =
    currentConstraint?.constraint === 'permission' ||
    (currentConstraint?.constraint === 'obligation' &&
      currentConstraint?.cultureType === 'intermediate')
      ? t('simulation.step.parcelConstraints.intermediateCultures')
      : t('simulation.step.parcelConstraints.mainCultures');

  const CULTURE_TYPES = useMemo(() => getCultureTypes(), []);
  const filterProperties = useMemo(
    () => [
      {
        field: 'constraint',
        label: t('simulation.step.parcelConstraints.filterConstraintType'),
        type: 'select',
        options: getConstraintTypes(),
      },
      {
        field: 'years',
        label: t('common.campaigns'),
        type: 'select',
        options: years.map((c) => ({ value: c.id, label: c.label })),
      },
      {
        field: 'parcels',
        label: t('common.parcels'),
        type: 'select',
        options: availableParcels.map((p) => ({ value: p.id, label: p.name })),
      },
      {
        field: 'cultures',
        label: t('common.cultures'),
        type: 'select',
        options: selectedCultures.map((c) => ({ value: c.id, label: c.name })),
      },
    ],
    [t, years, availableParcels, selectedCultures],
  );

  const handleToggleAll = (items, selectedIds, field) => {
    const itemIds = items.map((item) => item.id);
    const isAllSelected = itemIds.every((id) => selectedIds?.includes(id));
    itemIds.forEach((id) => {
      if (isAllSelected || !selectedIds?.includes(id)) {
        toggleParcelConstraintField(field, id);
      }
    });
  };

  const handleApplyFilters = (newFilters) => {
    setFilters(newFilters);
    const filtersList = FILTER_FIELDS.map((field, i) => {
      if (field === 'name' && newFilters.name) {
        return { id: i + 1, property: field, textValue: newFilters.name, values: [] };
      }
      if (field !== 'name' && newFilters[field]?.length > 0) {
        return { id: i + 1, property: field, values: newFilters[field] };
      }
      return null;
    }).filter(Boolean);
    setActiveFiltersList(filtersList);
  };

  const handleResetFilters = () => {
    setFilters({ name: '', years: [], parcels: [], constraint: [], cultures: [] });
    setActiveFiltersList([]);
  };

  const hasActiveFilters =
    filters.name ||
    filters.years.length > 0 ||
    filters.parcels.length > 0 ||
    filters.constraint.length > 0 ||
    filters.cultures?.length > 0;

  const activeFiltersCount =
    filters.years.length +
    filters.parcels.length +
    filters.constraint.length +
    (filters.cultures?.length || 0) +
    (filters.name ? 1 : 0);

  const filteredConstraints = parcelConstraints.filter((constraint) => {
    if (filters.name && !constraint.name?.toLowerCase().includes(filters.name.toLowerCase())) {
      return false;
    }
    if (filters.years.length > 0 && !constraint.years?.some((c) => filters.years.includes(c))) {
      return false;
    }
    if (
      filters.parcels.length > 0 &&
      !constraint.parcels?.some((p) => filters.parcels.includes(p))
    ) {
      return false;
    }
    if (filters.constraint.length > 0 && !filters.constraint.includes(constraint.constraint)) {
      return false;
    }
    if (
      filters.cultures?.length > 0 &&
      !constraint.cultures?.some((c) => filters.cultures.includes(c))
    ) {
      return false;
    }
    return true;
  });

  const constraintsForMap = hasActiveFilters ? filteredConstraints : parcelConstraints;

  const parcelsFiletered = hasActiveFilters
    ? filteredConstraints.flatMap((c) => c.parcels || [])
    : parcelConstraints.flatMap((c) => c.parcels || []);

  const parcelsToHighlight = useMemo(() => {
    if (hoveredParcels) return hoveredParcels;
    if (hasActiveFilters) return filteredConstraints.flatMap((c) => c.parcels || []);
    return null;
  }, [hoveredParcels, hasActiveFilters, filteredConstraints]);

  const FilterButton = (
    <div className="headerFilterWrapper">
      <button
        className={`constraintFilterBtn ${hasActiveFilters ? 'active' : ''}`}
        onClick={(e) => setFilterOpen(e.currentTarget)}
      >
        <FilterListIcon sx={{ fontSize: 18 }} />
        {t('common.filters')}
        {hasActiveFilters && <span className="constraintFilterBadge">{activeFiltersCount}</span>}
      </button>
      {hasActiveFilters && (
        <button className="constraintFilterClear" onClick={handleResetFilters}>
          {t('common.clear')}
        </button>
      )}
    </div>
  );

  return (
    <div className="stepParcelConstraint">
      <ModernCard
        title={t('simulation.step.parcelConstraints.title')}
        icon={PlaylistAddCheckIcon}
        headerLeft={FilterButton}
        headerAction={
          <ModernButton variant="secondary" onClick={() => toggleModal(true)}>
            {t('simulation.step.parcelConstraints.addConstraint')}
          </ModernButton>
        }
      >
        <div className="parcelConstraintLayout">
          <div className="parcelConstraintList">
            <Popover
              open={Boolean(filterOpen)}
              anchorEl={filterOpen}
              onClose={() => setFilterOpen(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
              <FilterPanel
                filterProperties={filterProperties}
                activeFilters={activeFiltersList}
                onApply={handleApplyFilters}
                onReset={handleResetFilters}
                onClose={() => setFilterOpen(null)}
                showTargetSelect={false}
                formatFiltersOutput={(filtersData) => {
                  const formattedFilters = {
                    name: '',
                    constraint: [],
                    years: [],
                    parcels: [],
                    cultures: [],
                  };
                  filtersData.forEach((filter) => {
                    if (filter.property === 'name' && filter.textValue) {
                      formattedFilters.name = filter.textValue;
                    } else if (filter.property && filter.values.length > 0) {
                      formattedFilters[filter.property] = filter.values;
                    }
                  });
                  return formattedFilters;
                }}
              />
            </Popover>

            {filteredConstraints.length === 0 ? (
              <div className="listPlaceholder">
                <PlaylistAddCheckIcon
                  sx={{
                    fontSize: 48,
                    color: '#94a3b8',
                    display: 'block',
                    margin: '0 auto 0.75rem',
                  }}
                />
                <p className="listPlaceholderTitle">
                  {hasActiveFilters
                    ? t('simulation.common.noResults')
                    : t('simulation.step.parcelConstraints.noConstraints')}
                </p>
                <p className="listPlaceholderSubtitle">
                  {hasActiveFilters
                    ? t('simulation.step.parcelConstraints.modifyFilters')
                    : t('simulation.step.parcelConstraints.addConstraintsPrompt')}
                </p>
              </div>
            ) : (
              <div className="constraintsList">
                {filteredConstraints.map((constraint) => (
                  <div
                    key={constraint.id}
                    onMouseEnter={() => setHoveredParcels(constraint.parcels)}
                    onMouseLeave={() => setHoveredParcels(null)}
                  >
                    <ConstraintCard
                      constraint={constraint}
                      exploitationSelected={exploitationSelected}
                      selectedCultures={selectedCultures}
                      years={years}
                      editConstraint={editConstraint}
                      deleteConstraint={deleteConstraint}
                      t={t}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          {mapCoordinates && (
            <div className="parcelConstraintMap">
              <div className="mapConsultativeContainer">
                <MapParamsSimulation
                  updateParcel={() => {}}
                  forecastCampaign={exploitationSelected?.forecastCampaign}
                  name="map_parcels_consultative"
                  fromProcedure={true}
                  parcelListData={availableParcelsWithChildren}
                  selectedParcels={parcelsFiletered}
                  highlightedParcels={parcelsToHighlight}
                  constraints={constraintsForMap}
                  cultures={selectedCultures}
                  readOnly={true}
                />
              </div>
            </div>
          )}
        </div>

        {showConstraintModal && (
          <div className="modalOverlay">
            <div className="modalContent" onClick={(e) => e.stopPropagation()}>
              <div className="modalHeader">
                <h2>
                  {currentConstraint?.id
                    ? t('simulation.step.parcelConstraints.editConstraintTitle')
                    : t('simulation.step.parcelConstraints.addConstraintTitle')}
                </h2>
                <div className="modalHeaderActions">
                  <ModernButton variant="secondary" onClick={() => toggleModal(false)}>
                    {t('common.cancel')}
                  </ModernButton>
                  <ModernButton variant="primary" onClick={() => saveConstraint()}>
                    {t('common.save')}
                  </ModernButton>
                  <button className="modalClose" onClick={() => toggleModal(false)}>
                    <CloseIcon sx={{ fontSize: 20 }} />
                  </button>
                </div>
              </div>

              <div className={`modalBody ${mapCoordinates ? 'withMap' : ''}`}>
                <div className="modalFormColumn">
                  <div className="modalSection">
                    <label className="modalLabel">
                      {t('simulation.step.parcelConstraints.constraintName')}
                    </label>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder={t('simulation.step.parcelConstraints.constraintNamePlaceholder')}
                      value={currentConstraint?.name || ''}
                      onChange={(e) => updateConstraintField('name', e.target.value)}
                      className="modalTextInput"
                    />
                  </div>

                  <div className="modalSection">
                    <label className="modalLabel">
                      {t('simulation.step.parcelConstraints.constraintType')}
                    </label>
                    <div className="modalButtonGroup">
                      {availableConstraintTypes.map(({ value, label }) => (
                        <button
                          key={value}
                          className={`modalTypeButton ${currentConstraint?.constraint === value ? 'active' : ''}`}
                          onClick={() =>
                            updateConstraintField('constraint', value, {
                              conversionBioYearId:
                                value === 'conversion' ? conversionBioYear?.id : undefined,
                            })
                          }
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {currentConstraint?.constraint === 'obligation' && (
                    <div className="modalSection">
                      <label className="modalLabel">
                        {t('simulation.step.parcelConstraints.cultureType')}
                      </label>
                      <div className="modalButtonGroup">
                        {CULTURE_TYPES.map(({ value, label }) => (
                          <button
                            key={value}
                            className={`modalTypeButton ${currentConstraint?.cultureType === value ? 'active' : ''}`}
                            onClick={() => updateConstraintField('cultureType', value)}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {currentConstraint?.constraint !== 'conversion' && (
                    <div className="modalSection">
                      <div className="modalLabelRow">
                        <label className="modalLabel">{cultureLabel}</label>
                        <button
                          type="button"
                          className="modalSelectAllBtn"
                          onClick={() =>
                            handleToggleAll(
                              filteredCultures,
                              currentConstraint?.cultures,
                              'cultures',
                            )
                          }
                        >
                          {filteredCultures.every((c) =>
                            currentConstraint?.cultures?.includes(c.id),
                          )
                            ? t('simulation.common.deselectAll')
                            : t('common.selectAll')}
                        </button>
                      </div>
                      <MultiSelectComponent
                        items={filteredCultures}
                        selectedIds={currentConstraint?.cultures || []}
                        onToggle={(value) => toggleParcelConstraintField('cultures', value)}
                        placeholder={t('simulation.step.parcelConstraints.selectCulture')}
                        renderItemLabel={(c) => c?.name}
                      />
                    </div>
                  )}

                  <div className="modalSection">
                    {currentConstraint?.constraint !== 'conversion' && (
                      <div className="modalLabelRow">
                        <label className="modalLabel">{t('common.campaigns')} *</label>
                        <button
                          type="button"
                          className="modalSelectAllBtn"
                          onClick={() => handleToggleAll(years, currentConstraint?.years, 'years')}
                        >
                          {years.every((y) => currentConstraint?.years?.includes(y.id))
                            ? t('simulation.common.deselectAll')
                            : t('common.selectAll')}
                        </button>
                      </div>
                    )}
                    {currentConstraint?.constraint === 'conversion' && (
                      <label className="modalLabel">
                        {t('simulation.step.parcelConstraints.conversionCampaign')} *
                      </label>
                    )}
                    <div className="modalCheckboxGrid">
                      {years.map((year, index) => (
                        <ModernCheckbox
                          key={year.id}
                          label={year.label}
                          checked={currentConstraint?.years?.includes(year.id)}
                          onChange={() => {
                            if (currentConstraint?.constraint === 'conversion') {
                              updateConstraintField('years', [year.id]);
                            } else {
                              toggleParcelConstraintField('years', year.id);
                            }
                          }}
                          disabled={currentConstraint?.constraint === 'conversion' && index < 2}
                        />
                      ))}
                    </div>
                  </div>

                  <div className="modalSection">
                    <div className="modalLabelRow">
                      <label className="modalLabel">
                        {t('simulation.step.parcelConstraints.parcelsLabel', {
                          count: currentConstraint?.parcels?.length || 0,
                          surface: selectedParcelsSurface,
                        })}
                      </label>
                      <button
                        type="button"
                        className="modalSelectAllBtn"
                        onClick={() =>
                          handleToggleAll(availableParcels, currentConstraint?.parcels, 'parcels')
                        }
                      >
                        {availableParcels.every((p) => currentConstraint?.parcels?.includes(p.id))
                          ? t('simulation.common.deselectAll')
                          : t('common.selectAll')}
                      </button>
                    </div>
                    <MultiSelectComponent
                      items={availableParcels}
                      selectedIds={currentConstraint?.parcels || []}
                      onToggle={(value) => toggleParcelConstraintField('parcels', value)}
                      placeholder={t('simulation.step.parcelConstraints.selectParcel')}
                      renderItemLabel={(p) => `${p?.name} (${p?.surface} ha)`}
                      tagClassName="modalSelectedTag modalSelectedTagParcel"
                    />
                  </div>
                </div>

                {mapCoordinates && (
                  <div className="modalMapColumn">
                    <div className="modalMapHeader">
                      <label className="modalLabel">{t('simulation.common.parcelSelection')}</label>
                    </div>
                    <div className="modalMapContainer">
                      <MapParamsSimulation
                        updateParcel={(parcelId) =>
                          toggleParcelConstraintField('parcels', parcelId)
                        }
                        forecastCampaign={exploitationSelected?.forecastCampaign}
                        name="map_parcels_modal"
                        fromProcedure={true}
                        parcelListData={availableParcelsWithChildren}
                        selectedParcels={currentConstraint?.parcels || []}
                        showIrrigable={true}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </ModernCard>
    </div>
  );
};

export default StepParcelConstraint;
